import {
  Check,
  Download,
  FolderOpen,
  History,
  MapPin,
  Search,
  Sparkles,
} from "lucide-react";

import { StandardPageLayout } from "@/components/layouts/standard-page-layout";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const plannedFeatures = [
  {
    icon: History,
    title: "One history. Your whole team.",
    description:
      "Share geocoding history across your organisation, pick up where a teammate left off, and export your results.",
  },
  {
    icon: FolderOpen,
    title: "A home for every project",
    description:
      "Group the addresses you geocode manually into projects, from site visits to your next big idea.",
  },
  {
    icon: Download,
    title: "Take your finds with you",
    description:
      "Export your history or a project's addresses as CSV or Excel, ready for whatever comes next.",
  },
];

export const VerifyGeocoderComingSoonPage = () => {
  return (
    <StandardPageLayout
      maxWidth="6xl"
      pageTitle={{
        icon: MapPin,
        title: "Geocoder Playground",
        description: "A new place to find, organise and share your addresses.",
      }}
    >
      <Card className="gap-0 p-0">
        <div className="grid lg:grid-cols-2">
          <div className="flex flex-col justify-center p-6 sm:p-10">
            <Badge variant="secondary" className="mb-5 gap-1.5 self-start">
              <Sparkles aria-hidden="true" />
              In the works
            </Badge>
            <h2 className="max-w-md text-3xl font-semibold tracking-tight sm:text-4xl">
              Your addresses are going places.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              An address in, a location out. We&apos;re building a visual geocoder
              where you can search addresses, find them on the map, and keep your
              team&apos;s discoveries together.
            </p>
            <p className="mt-6 flex items-center gap-2 text-sm font-medium">
              <MapPin className="size-4 text-primary" aria-hidden="true" />
              Bring the addresses. We&apos;re building the playground.
            </p>
          </div>

          <div
            aria-hidden="true"
            className="relative m-4 min-h-80 overflow-hidden rounded-xl border bg-primary/5 sm:m-6 lg:ml-0"
          >
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            <div className="absolute -right-8 top-12 size-48 rounded-full bg-chart-1/10" />
            <div className="absolute -left-10 bottom-0 size-40 rounded-full bg-primary/10" />
            <div className="absolute left-[28%] top-28 h-20 w-[40%] -rotate-12 rounded-tr-3xl border-r-2 border-t-2 border-dashed border-primary/30" />

            <div className="absolute inset-x-5 top-5 flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm sm:inset-x-8">
              <Search className="size-4 shrink-0 text-primary" />
              <span className="text-sm text-muted-foreground">
                Every adventure starts with an address...
              </span>
            </div>

            <div className="absolute left-[21%] top-[35%] -rotate-12 rounded-full border bg-card p-3 text-primary shadow-sm">
              <MapPin className="size-7" />
            </div>
            <div className="absolute right-[22%] top-[30%] rotate-12 rounded-full border bg-card p-2.5 text-primary shadow-sm">
              <MapPin className="size-6" />
            </div>
            <div className="absolute right-[35%] top-[51%] rounded-full border bg-card p-2 text-primary shadow-sm">
              <MapPin className="size-5" />
            </div>
            <div className="absolute left-[38%] top-[38%] flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs font-medium shadow-sm">
              <Check className="size-3 text-success" />
              Found its place!
            </div>

            <div className="absolute inset-x-5 bottom-5 -rotate-2 rounded-lg border bg-card p-4 shadow-sm sm:inset-x-8">
              <div className="flex items-center gap-3">
                <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <FolderOpen className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">Friday field trip</p>
                  <p className="text-xs text-muted-foreground">
                    A few good addresses. One tidy project.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 border-t pt-3">
                <span className="mr-auto text-xs text-muted-foreground">
                  Ready to roam
                </span>
                <Badge variant="outline">CSV</Badge>
                <Badge variant="outline">Excel</Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 border-t bg-muted/20 p-6 sm:p-8 md:grid-cols-3">
          {plannedFeatures.map(({ icon: Icon, title, description }) => (
            <div key={title}>
              <Icon className="mb-3 size-5 text-primary" aria-hidden="true" />
              <h3 className="text-sm font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {description}
              </p>
            </div>
          ))}
        </div>
      </Card>
    </StandardPageLayout>
  );
};
